import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Typography, Paper } from '@mui/material';
import { createSocket, SocketType } from './utils/socket';
import ChatInterface from './components/ChatInterface';
import LoginForm from './components/LoginForm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { useMaster } from './hooks/useMaster';
import { config } from './config';
import './App.css';

// Tema personalizado para Samael
const samaelTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8B5CF6', // Púrpura místico
      light: '#A78BFA',
      dark: '#7C3AED',
    },
    secondary: {
      main: '#F59E0B', // Dorado
      light: '#FBBF24',
      dark: '#D97706',
    },
    background: {
      default: '#0F0F23', // Azul muy oscuro
      paper: '#1A1A2E', // Azul oscuro
    },
    text: {
      primary: '#E5E7EB',
      secondary: '#9CA3AF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1A1A2E',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isGuestMode } = useAuth();
  const { personality } = useMaster();
  const [socket, setSocket] = useState<SocketType | null>(null);
  
  // Header colors based on personality
  const headerGradient = personality?.colors?.headerStart && personality?.colors?.headerEnd
    ? `linear-gradient(90deg, ${personality.colors.headerStart} 0%, ${personality.colors.headerEnd} 100%)`
    : 'linear-gradient(90deg, #8B5CF6 0%, #A78BFA 100%)';

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setSocket((current) => {
        if (current) {
          current.close();
        }
        return null;
      });
      return;
    }

    const socketUrl =
      process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production'
        ? window.location.origin
        : 'http://localhost:3000');
    const newSocket = createSocket(user.id.toString(), socketUrl);

    newSocket.on('connect', () => {
      newSocket.emit('join', user.id.toString());
    });

    newSocket.on('connect_error', (error: any) => {
      console.error('Error de conexión WebSocket:', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
      setSocket(null);
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (personality?.name) {
      const baseTitle = (personality as any)?.headerTitle || personality.name;
      document.title = baseTitle;
    } else {
      document.title = 'Portal Mystic Tarot';
    }
  }, [personality]);

  // Mostrar LoginForm solo si no está autenticado Y no está en modo guest
  if (!isAuthenticated && !isGuestMode) {
    return <LoginForm />;
  }

  return (
    <SocketProvider socket={socket}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${config.PUBLIC_URL}/images/bg.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            px: { xs: 1.5, md: 3 },
            py: { xs: 1.5, md: 2.5 },
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={24}
            sx={{
              width: '100%',
              minHeight: { xs: '100vh', md: 'calc(100vh - 48px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: 'rgba(26, 26, 46, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderRadius: { xs: 2, md: 3 },
              minWidth: 0,
              maxHeight: { xs: 'none', md: 'calc(100vh - 48px - env(safe-area-inset-bottom))' },
            }}
          >
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                pb: { xs: 2.5, sm: 3 },
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                background: headerGradient,
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={`${config.PUBLIC_URL}/images/logo.png`}
                  alt="Logo"
                  style={{ width: 'auto', height: '3rem', maxWidth: '80%' }}
                />
              </Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', sm: '2.25rem' },
                  lineHeight: 1.1,
                }}
              >
                {(personality as any)?.headerTitle || personality.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.95,
                  mt: { xs: 1, sm: 1.5 },
                  lineHeight: 1.5,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                }}
              >
                {(personality as any)?.headerSubtitle || 'Tu guía espiritual personal está aquí para ayudarte.'}
              </Typography>
            </Box>

            <ChatInterface />
          </Paper>
        </Container>
      </Box>
    </SocketProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={samaelTheme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;